import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { produce } from 'immer';

import {
  ChapterWidget,
  ComponentListWidget,
  ComponentWidget,
  createChapterWidget,
  FlowchartWidget,
  getReferencesFromComponentListWidget,
  getReferencesFromFlowchartWidget,
  Guideline,
  GuidelineWidget,
  Reference,
  ReferenceWithNumber,
  TextWidget,
} from '@treetop/shared/model';

import { HtmlUtil } from '../../../../../feature-flowchart/src/lib/util/html.util';
import { flattenFlowchartElements } from '../../../../../model/src/lib/widgets/flowchart.model';

export type EditableGuidelineWidget = TextWidget | ChapterWidget | FlowchartWidget | ComponentListWidget | ComponentWidget;

export type GuidelineGraphNodeType = 'root' | 'chapter' | 'component-list' | 'flowchart' | 'text';

export type GuidelineGraphNode = {
  uniqueId: string;
  parentId: string | undefined;
  type: GuidelineGraphNodeType;
  children?: GuidelineGraphNode[];
  references?: Reference[];
};

export type GuidelineGraph = {
  root: GuidelineGraphNode;
};

export type WidgetMap = Record<string, WritableSignal<GuidelineWidget>>;

@Injectable()
export class GuidelineGraphService {
  private _graph = signal<GuidelineGraph | undefined>(undefined);
  public graph = this._graph.asReadonly();

  private _map = signal<WidgetMap>({});
  public map = this._map.asReadonly();

  public numberedChapters = computed(() => this.traverser()?.numberedChapters ?? {}, {
    equal: (a, b) => {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);

      if (aKeys.length !== bKeys.length) {
        return false;
      }

      for (const key of aKeys) {
        if (a[key] !== b[key]) {
          return false;
        }
      }

      return true;
    },
  });

  public flatGraph = computed(() => this.traverser()?.flatGraph ?? []);

  public orderedReferences = signal<ReferenceWithNumber[]>([]);

  public recalculateReferenceNumbers() {
    const flatGraph = this.flatGraph();
    const flatChapters = flatGraph.map((element) => this._map()[element.uniqueId]?.() as ChapterWidget).filter((widget) => !!widget);

    const references: Reference[] = [];

    flatChapters.forEach((chapter) => {
      references.push(...(chapter.references ?? []));

      chapter.widgetIds?.forEach((widgetId) => {
        const widget = this._map()[widgetId]?.();
        if (!widget) {
          return;
        }

        if (widget.widgetType === 'component-list') {
          const resolvedMap = Object.fromEntries(Object.entries(this._map()).map(([id, sig]) => [id, sig()]));
          references.push(...(getReferencesFromComponentListWidget(widget, resolvedMap) ?? []));
        } else if (widget.widgetType === 'flowchart') {
          references.push(...(getReferencesFromFlowchartWidget(widget) ?? []));
        } else {
          references.push(...(widget.references ?? []));
        }
      });
    });

    const uniqueReferences: Reference[] = [];
    const seenIds = new Set<string>();

    for (const ref of references.filter(Boolean)) {
      if (!seenIds.has(ref.id)) {
        seenIds.add(ref.id);
        uniqueReferences.push(ref);
      }
    }

    const orderedReferences = uniqueReferences.map((reference, index) => ({
      ...reference,
      referenceNumber: index + 1,
    }));

    this.orderedReferences.set(orderedReferences);
  }

  private traverser = computed(() => {
    const graph = this._graph();

    if (!graph) return;

    const numberedChapters: Record<string, string> = {};
    const flatGraph: GuidelineGraphNode[] = [];

    const traverseNode = (node: GuidelineGraphNode, number: string): GuidelineGraphNode => {
      flatGraph.push(node);

      const isChapter = node.type === 'chapter';
      if (isChapter) {
        numberedChapters[node.uniqueId] = number;
      }

      const childrenNodes: GuidelineGraphNode[] = [];
      const componentsNodes: GuidelineGraphNode[] = [];

      node.children?.forEach((child, index) => {
        const childNumber = `${number}.${index + 1}`;
        const childNode = traverseNode(child, childNumber);
        if (childNode) {
          childrenNodes.push(childNode);
        }
      });

      return {
        ...node,
        ...(isChapter
          ? { children: childrenNodes, guidelineComponents: componentsNodes }
          : { children: componentsNodes, guidelineComponents: [] }),
      };
    };

    graph.root.children?.forEach((child, index) => traverseNode(child, `${index + 1}`));

    return {
      numberedChapters,
      flatGraph,
    };
  });

  public createGuidelineGraph(guideline: Guideline) {
    // TODO: Temporary solution for lost parentIds in text widgets!
    const newMap: WidgetMap = Object.fromEntries(
      Object.entries(guideline.widgetMap ?? {}).map(([id, widget]) => {
        if (widget.widgetType === 'text') {
          const updatedWidget = { ...widget };
          if (id.endsWith('-description')) {
            updatedWidget.parentId = id.substring(0, id.length - '-description'.length);
          } else if (id.endsWith('-description-below')) {
            updatedWidget.parentId = id.substring(0, id.length - '-description-below'.length);
          }
          return [id, signal(updatedWidget)];
        }
        return [id, signal(widget)];
      }),
    );

    const createChapterNode = (chapterWidgetId: string, parentId: string | null = null): GuidelineGraphNode => {
      const chapter = newMap[chapterWidgetId]?.() as ChapterWidget; // signal unwrap with ()

      const children = chapter.childrenIds?.map((childId) => createChapterNode(childId, chapter.uniqueId)) || [];

      return {
        uniqueId: chapter.uniqueId,
        type: 'chapter',
        parentId,
        children,
      };
    };

    const guidelineUniqueId = guideline.uniqueId ?? '0000-0000-0000-0000';

    const root: GuidelineGraphNode = {
      uniqueId: guidelineUniqueId,
      type: 'root',
      parentId: undefined,
      children: guideline.chaptersWidgetIds?.map((id) => createChapterNode(id, guidelineUniqueId)) || [],
    };

    this._graph.set({ root });
    this._map.set(newMap);

    this.recalculateReferenceNumbers();
  }

  public updateElement(id: string, data: GuidelineWidget) {
    const elementSignal = this._map()[id];

    if (!elementSignal) {
      console.warn(`Element with uniqueId ${id} not found in the map.`);
      return;
    }

    elementSignal.set(data);

    this.recalculateReferenceNumbers();
  }

  public addWidget(widget: GuidelineWidget, parentId?: string, index?: number) {
    this.addWidgetToMap(parentId ? { ...widget, parentId } : widget);

    if (parentId) {
      const parentSignal = this._map()[parentId];
      if (!parentSignal) {
        console.warn(`Parent element with uniqueId ${parentId} not found in the map.`);
        return;
      }
      const parent = parentSignal();
      if (parent.widgetType === 'chapter' || parent.widgetType === 'component-list' || parent.widgetType === 'component') {
        const updatedParent = produce(parent, (draft) => {
          const isChildComponent = draft.widgetType === 'component' && widget.widgetType === 'component';
          const ids = isChildComponent ? (draft.childrenIds ??= []) : (draft.widgetIds ??= []);
          if (index !== undefined && index >= 0 && index <= ids.length) {
            ids.splice(index, 0, widget.uniqueId);
          } else {
            ids.push(widget.uniqueId);
          }
        });
        this.updateElement(parentId, updatedParent);
      } else {
        console.warn(`Parent element with uniqueId ${parentId} is not a valid container for widgets.`);
      }
    }
  }

  public addChapter(data?: Partial<ChapterWidget>, parentUniqueId?: string, index?: number) {
    const chapterWidget: ChapterWidget = createChapterWidget({
      parentId: parentUniqueId ?? undefined,
      ...data,
    });

    const newNode: GuidelineGraphNode = {
      uniqueId: chapterWidget.uniqueId,
      type: 'chapter',
      parentId: parentUniqueId,
      references: [],
      children: [],
    };

    this._graph.update((graph) => {
      if (!graph) return;

      const insertRec = (currentNode: GuidelineGraphNode): GuidelineGraphNode => {
        if (currentNode.uniqueId === parentUniqueId) {
          return {
            ...currentNode,
            children: insertNode(newNode, currentNode.children ?? [], index),
          };
        }
        return {
          ...currentNode,
          children: currentNode.children?.map(insertRec) ?? [],
        };
      };

      const updatedRoot = parentUniqueId
        ? insertRec(graph.root)
        : {
            ...graph.root,
            children: insertNode(newNode, graph.root.children ?? [], index),
          };

      return { ...graph, root: updatedRoot };
    });

    this.addWidgetToMap(chapterWidget);

    if (parentUniqueId) {
      const parentElement = this._map()[parentUniqueId]?.() as ChapterWidget;
      if (!chapterWidget.uniqueId) return;

      const newChildId = chapterWidget.uniqueId;
      const currentChildrenIds = parentElement.childrenIds ?? [];

      const newChildrenIds =
        index !== undefined && index >= 0 && index <= currentChildrenIds.length
          ? [...currentChildrenIds.slice(0, index), newChildId, ...currentChildrenIds.slice(index)]
          : [...currentChildrenIds, newChildId];

      const updatedParent = produce(parentElement, (draft) => {
        draft.childrenIds = newChildrenIds;
      });

      this.updateElement(parentUniqueId, updatedParent);
    }
  }

  public moveWidget(widgetId: string, parentWidgetId: string | null, index?: number): void {
    const widgetSignal = this._map()[widgetId];
    if (!widgetSignal) {
      console.warn(`Widget with uniqueId ${widgetId} not found in the map.`);
      return;
    }

    const widget = widgetSignal();
    if (!widget) {
      console.warn(`Widget with uniqueId ${widgetId} has no data in the map.`);
      return;
    }

    const oldParentId = widget.parentId;

    if (oldParentId && parentWidgetId && oldParentId === parentWidgetId) {
      const parentSignal = this._map()[oldParentId];
      if (!parentSignal) {
        return;
      }

      const parent = parentSignal();

      const updatedParent = produce(parent, (draft) => {
        const list =
          draft.widgetType === 'chapter' || draft.widgetType === 'component-list'
            ? draft.widgetIds
            : 'childrenIds' in draft
              ? draft.childrenIds
              : undefined;

        if (!list) {
          return;
        }

        const fromIndex = list.indexOf(widgetId);
        if (fromIndex === -1) return;

        list.splice(fromIndex, 1);

        const targetIndex = index ?? list.length;

        list.splice(targetIndex, 0, widgetId);
      });

      this.updateElement(oldParentId, updatedParent);
      return;
    }

    if (oldParentId) {
      const oldParentSignal = this._map()[oldParentId];
      if (oldParentSignal) {
        const oldParent = oldParentSignal();

        const updatedOldParent = produce(oldParent, (draft) => {
          const list =
            draft.widgetType === 'chapter' || draft.widgetType === 'component-list'
              ? draft.widgetIds
              : 'childrenIds' in draft
                ? draft.childrenIds
                : undefined;

          if (list) {
            const idx = list.indexOf(widgetId);
            if (idx > -1) list.splice(idx, 1);
          }
        });

        this.updateElement(oldParentId, updatedOldParent);
      }
    }

    if (parentWidgetId) {
      const newParentSignal = this._map()[parentWidgetId];
      if (newParentSignal) {
        const newParent = newParentSignal();

        if (newParent.widgetType !== 'chapter' && newParent.widgetType !== 'component-list' && newParent.widgetType !== 'component') {
          return;
        }

        const updatedNewParent = produce(newParent, (draft) => {
          const list =
            draft.widgetType === 'chapter' || draft.widgetType === 'component-list' ? (draft.widgetIds ??= []) : (draft.childrenIds ??= []);

          if (index === undefined || index < 0 || index > list.length) {
            list.push(widgetId);
          } else {
            list.splice(index, 0, widgetId);
          }
        });

        this.updateElement(parentWidgetId, updatedNewParent);
      }
    }

    this.updateElement(widgetId, { ...widget, parentId: parentWidgetId });
  }

  public moveChapter(chapterId: string, newParentChapterId: string | null, index?: number): void {
    const newGraph = produce(this._graph(), (draft) => {
      if (!draft) return;

      const findNode = (node: GuidelineGraphNode, id: string | null): GuidelineGraphNode | undefined => {
        if (!id) return undefined;
        if (node.uniqueId === id) return node;
        return node.children?.map((c) => findNode(c, id)).find(Boolean);
      };

      const chapter = findNode(draft.root, chapterId);
      if (!chapter) return;

      const oldParent = chapter.parentId ? findNode(draft.root, chapter.parentId) : draft.root;
      const newParent = newParentChapterId ? findNode(draft.root, newParentChapterId) : draft.root;
      if (!oldParent || !newParent) return;

      // Remove from old parent
      const oldIndex = oldParent.children.findIndex((c) => c.uniqueId === chapterId);
      if (oldIndex !== -1) oldParent.children.splice(oldIndex, 1);

      // Update parentId
      chapter.parentId = newParent === draft.root ? null : newParent.uniqueId;

      // Insert into new parent
      newParent.children ??= [];
      if (index === undefined || index < 0 || index > newParent.children.length) {
        newParent.children.push(chapter);
      } else {
        newParent.children.splice(index, 0, chapter);
      }

      // Update reactive map
      const syncChildrenIds = (parent: GuidelineGraphNode) => {
        if (parent === draft.root) return;
        const element = this._map()[parent.uniqueId]?.() as ChapterWidget;
        if (!element) return;
        const updated = produce(element, (draft) => {
          draft.childrenIds = parent.children.map((c) => c.uniqueId);
        });
        this.updateElement(parent.uniqueId, updated);
      };

      syncChildrenIds(oldParent);
      syncChildrenIds(newParent);
    });

    this._graph.set(newGraph);
  }

  public removeWidget(uniqueId: string): string[] {
    const removedIds = new Set<string>();

    const collectAll = (id: string) => {
      if (removedIds.has(id)) {
        return;
      }

      removedIds.add(id);

      const widgetSignal = this._map()[id];
      if (!widgetSignal) {
        return;
      }

      const widget = widgetSignal();
      if (!widget) {
        return;
      }

      if ('childrenIds' in widget && Array.isArray(widget.childrenIds)) {
        widget.childrenIds.forEach(collectAll);
      }

      if ('widgetIds' in widget && Array.isArray(widget.widgetIds)) {
        widget.widgetIds.forEach(collectAll);
      }
    };

    collectAll(uniqueId);

    // Remove widget and children from map
    this._map.update((map) => {
      for (const id of removedIds) {
        delete map[id];
      }
      return map;
    });

    const currentMap = this._map();
    for (const widgetSignal of Object.values(currentMap)) {
      const widget = widgetSignal();
      if (!widget) continue;

      let updated: GuidelineWidget = widget;

      if ('childrenIds' in updated && Array.isArray((updated as ChapterWidget).childrenIds)) {
        const childrenIds = (updated as ChapterWidget).childrenIds!;
        const filtered = childrenIds.filter((id) => !removedIds.has(id));
        if (filtered.length !== childrenIds.length) {
          updated = { ...updated, childrenIds: filtered } as GuidelineWidget;
        }
      }

      if ('widgetIds' in updated && Array.isArray((updated as ChapterWidget).widgetIds)) {
        const widgetIds = (updated as ChapterWidget).widgetIds!;
        const filtered = widgetIds.filter((id) => !removedIds.has(id));
        if (filtered.length !== widgetIds.length) {
          updated = { ...updated, widgetIds: filtered } as GuidelineWidget;
        }
      }

      if (updated !== widget) {
        widgetSignal.set(updated);
      }
    }
    this._map.update((map) => ({ ...map }));

    this.removeLinksByTargets(Array.from(removedIds));

    // TODO: Remove graph completely, use the map to reconstruct when needed
    this._graph.update((graph) => {
      if (!graph) return graph;

      const removeRec = (node: GuidelineGraphNode): GuidelineGraphNode | null => {
        if (removedIds.has(node.uniqueId)) {
          return null;
        }
        return {
          ...node,
          children: node.children?.map(removeRec).filter((child): child is GuidelineGraphNode => child !== null) ?? [],
        };
      };

      const updatedRoot = removeRec(graph.root);
      return updatedRoot ? { ...graph, root: updatedRoot } : undefined;
    });

    return Array.from(removedIds);
  }

  public addWidgetToMap(element: GuidelineWidget) {
    const id = element.uniqueId;

    if (!id) {
      console.warn('Element does not have a uniqueId, cannot add to map.');
      return;
    }

    this._map.update((map) => {
      map[id] = signal(element);
      return { ...map };
    });
  }

  public removeLinksByTargets(targetIds: string[]) {
    const targetIdsSet = new Set(targetIds);

    this._map.update((map) => {
      const newMap: typeof map = {};

      for (const [key, widgetSignal] of Object.entries(map)) {
        const widget = widgetSignal();
        if (!widget) {
          newMap[key] = widgetSignal;
          continue;
        }

        let updatedWidget = this.updateWidgetLinks(widget, targetIdsSet);

        if (updatedWidget.widgetType === 'flowchart') {
          updatedWidget = this.updateFlowchart(updatedWidget as FlowchartWidget, targetIdsSet);
        } else if (updatedWidget.widgetType === 'text') {
          updatedWidget = this.updateText(updatedWidget as TextWidget, targetIdsSet);
        }

        if (updatedWidget !== widget) {
          widgetSignal.set(updatedWidget);
        }

        newMap[key] = widgetSignal;
      }

      return newMap;
    });
  }

  private updateWidgetLinks(widget: GuidelineWidget, targetIds: Set<string>): GuidelineWidget {
    const links = this.filterLinks(widget.links ?? [], targetIds);

    if (links.length !== (widget.links ?? []).length) {
      return { ...widget, links };
    }

    return widget;
  }

  private updateFlowchart(widget: FlowchartWidget, targetIds: Set<string>): FlowchartWidget {
    return produce(widget, (draft) => {
      const elements = flattenFlowchartElements(draft);

      elements.forEach((element) => {
        const currentLinks = element.links ?? [];
        const newLinks = this.filterLinks(currentLinks, targetIds);

        if (newLinks.length !== currentLinks.length) {
          element.links = newLinks;
        }

        targetIds.forEach((targetId) => {
          if ('guidelineAction' in element && element.guidelineAction?.description) {
            element.guidelineAction.description = HtmlUtil.removeLinksFromHtml(element.guidelineAction.description, targetId);
          }

          if ('guidelineCriteria' in element && element.guidelineCriteria?.description) {
            element.guidelineCriteria.description = HtmlUtil.removeLinksFromHtml(element.guidelineCriteria.description, targetId);
          }

          if ('guidelineObservation' in element && element.guidelineObservation?.description) {
            element.guidelineObservation.description = HtmlUtil.removeLinksFromHtml(element.guidelineObservation.description, targetId);
          }
        });
      });
    });
  }

  private updateText(widget: TextWidget, targetIds: Set<string>): TextWidget {
    if (widget.text === undefined) {
      return widget;
    }

    let text = widget.text;

    for (const id of targetIds) {
      const newText = HtmlUtil.removeLinksFromHtml(text, id);
      if (newText !== text) {
        text = newText;
      }
    }

    return text !== widget.text ? { ...widget, text } : widget;
  }

  private filterLinks<T extends { toChapterUniqueId?: string; toElementUniqueId?: string }>(links: T[] = [], targetIds: Set<string>): T[] {
    return links.filter((link) => !targetIds.has(link.toChapterUniqueId) && !targetIds.has(link.toElementUniqueId));
  }
}

const insertNode = (node: GuidelineGraphNode, children: GuidelineGraphNode[], index?: number) => {
  const updated = [...children];
  if (index !== undefined) {
    updated.splice(index, 0, node);
  } else {
    updated.push(node);
  }
  return updated;
};
